// Hook para gerenciar Push Notifications do DNA GO
import { useState, useEffect, useCallback } from 'react';

const VAPID_PUBLIC_KEY = 'BNV4RAGHQeG-vqmm7Lcb0RsXnEGFnfFzgeijkbZN_e3OOOd3JQld_ORpMOAKwuXwxJxukGt_0F6eJXH6Z3HJIhQ';

export function usePushNotifications(responsavelId) {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Verificar suporte
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Registrar Service Worker
  const registerServiceWorker = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw-dnago.js');
      console.log('Service Worker registrado:', registration);
      return registration;
    } catch (err) {
      console.error('Erro ao registrar Service Worker:', err);
      throw err;
    }
  }, []);

  // Converter chave VAPID para Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Solicitar permissão e inscrever
  const subscribe = useCallback(async () => {
    if (!isSupported || !responsavelId) {
      setError('Push notifications não suportadas ou responsável não identificado');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Solicitar permissão
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        throw new Error('Permissão negada para notificações');
      }

      // Registrar Service Worker
      const registration = await registerServiceWorker();

      // Aguardar SW estar ativo
      await navigator.serviceWorker.ready;

      // Criar subscription
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      console.log('Push subscription criada:', sub);

      // Enviar para o backend
      const API_URL = window.location.origin;
      const response = await fetch(`${API_URL}/api/dnago/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          endpoint: sub.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('auth'))))
          },
          responsavel_id: responsavelId
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao registrar subscription no servidor');
      }

      setSubscription(sub);
      return sub;

    } catch (err) {
      console.error('Erro ao inscrever para push:', err);
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, responsavelId, registerServiceWorker]);

  // Cancelar inscrição
  const unsubscribe = useCallback(async () => {
    if (!subscription) return;

    setIsLoading(true);
    try {
      await subscription.unsubscribe();

      const API_URL = window.location.origin;
      await fetch(`${API_URL}/api/dnago/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setSubscription(null);
    } catch (err) {
      console.error('Erro ao cancelar inscrição:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [subscription]);

  // Verificar subscription existente
  useEffect(() => {
    const checkExistingSubscription = async () => {
      if (!isSupported) return;

      try {
        const registration = await navigator.serviceWorker.getRegistration('/sw-dnago.js');
        if (registration) {
          const existingSub = await registration.pushManager.getSubscription();
          if (existingSub) {
            setSubscription(existingSub);
          }
        }
      } catch (err) {
        console.error('Erro ao verificar subscription:', err);
      }
    };

    checkExistingSubscription();
  }, [isSupported]);

  return {
    isSupported,
    permission,
    subscription,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    isSubscribed: !!subscription
  };
}

export default usePushNotifications;
